/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 08-09-2026
 *
 * De actietabel: per modelactie op één plek wat de drie rollen ermee doen.
 *
 * In AngularJS waren dit **twee** switches met verschillende actienamen: de leidertak ontving
 * *verzoeken* (`addNode`, `toggleQValue`, …), de volgertak *uitvoeringen* (`create`, `delete`,
 * `setModeldata`, …), en achttien namen kwamen aan beide kanten voor met een andere betekenis.
 * Daarnaast stond de lokale kant nog eens apart in de `plan…`-functies. Die drie zijn hier per actie
 * naast elkaar gezet:
 *
 * | veld | wie | wanneer |
 * |---|---|---|
 * | `vraag` | een volger | in plaats van uitvoeren: het verzoek dat naar de leider gaat |
 * | `lees` | de leider | een binnengekomen verzoek vertalen naar de argumenten van `uitvoer` |
 * | `uitvoer` | leider of solo | de actie zelf, ook namens een volger |
 * | `vanLeider` | een volger | wat de leider gedaan heeft overnemen |
 *
 * De staart — undo-punt, loggen, versturen, snapshot, opslaan — staat níet in de tabel maar in de
 * `SamenwerkprotocolService`, in één vaste volgorde. Wat de tabel per actie wél vastlegt is het
 * *beleid*: of er een undo-punt voor moet.
 */
import type {NodeSingular, Position} from 'cytoscape';
import type {Actieuitkomst, Modelacties} from './modelacties';
import type {Samenwerkactie} from './protocol.types';
import type {DlModel, NieuweNodeData} from '../model/dl-model';
import type {NormmatchOutput} from 'dynalearn-common/lib/normmatch.types';
import type {Modelproperties, OpslagService} from '../model/opslag.service';
import type {NormService} from '../engines/norm.service';
import type {BouwsuggestieEngineService} from '../engines/bouwsuggestie-engine.service';
import type {CondtagState} from '../../services/condtag.service';
import type {FeedbackbalkService} from '../../services/feedbackbalk.service';
import type {BouwsuggestieSoort, FeedbackSelectieService} from '../../services/feedback-selectie.service';
import type {ToastService} from '../../services/toast.service';
import type {Sihwlog} from 'sihw-ngx';

/**
 * De argumenten van een actie zoals de lokale aanroeper ze geeft en `uitvoer` ze verwacht. Per actie
 * een andere vorm; de tabelregel weet welke.
 */
export type Actieargumenten = Record<string, unknown>;

/**
 * Wat er bij een binnengekomen bericht hoort, los van de actie zelf.
 */
export interface Berichtcontext {
    /** De client die de reeks begon; de leider logt op zijn naam. */
    afzender?: string;
    /** Of wij die client waren. Zo ja, dan selecteren wij wat er gemaakt wordt. */
    origineleAfzenderJij: boolean;
}

/**
 * Eén regel van de actietabel.
 */
export interface Actieregel {
    /** Of er vóór de uitvoering een undo-punt gezet wordt. */
    undo?: boolean;
    /** Wat een volger naar de leider stuurt in plaats van zelf uit te voeren; ontbreekt: zelf doen. */
    vraag?: (args: Actieargumenten) => Samenwerkactie;
    /** Vertaalt een verzoek van een volger naar argumenten voor `uitvoer`; `null` = overslaan. */
    lees?: (stap: Samenwerkactie) => Actieargumenten | null;
    /** Voert de actie uit, lokaal of als leider namens een volger. */
    uitvoer?: (args: Actieargumenten, volger?: string | null) => Actieuitkomst;
    /** Voert uit wat de leider gedaan heeft; ontbreekt: deze actie komt nooit van een leider. */
    vanLeider?: (stap: Samenwerkactie, context: Berichtcontext) => Actieuitkomst;
}

/**
 * Wat de tabel nodig heeft om zijn werk te doen.
 */
export interface Tabelomgeving {
    acties: Modelacties;
    dlModel: DlModel;
    opslag: OpslagService;
    normService: NormService;
    feedbackbalk: FeedbackbalkService;
    feedbackSelectie: FeedbackSelectieService;
    bouwsuggestieEngine: BouwsuggestieEngineService;
    /** De meldingen; de weigering van een verzoek komt als toast bij de vrager terug. */
    toast: ToastService;
    /** Brengt de volgers bij: de undostand, de norm, de conditiesets en een verse suggestie-run. */
    syncNaarVolgers: () => void;
    log: Sihwlog;
}

/**
 * Bouwt de actietabel. Een functie en geen klasse: de regels zijn data, en wat ze nodig hebben komt
 * uit de omgeving die de baas meegeeft.
 */
export function maakActietabel(omgeving: Tabelomgeving): Map<string, Actieregel> {
    const {acties, dlModel, opslag, normService, feedbackbalk, feedbackSelectie, bouwsuggestieEngine,
        toast, syncNaarVolgers, log} = omgeving;

    /**
     * De node bij een berichtstap, of `null` als wij hem niet (meer) hebben.
     */
    const nodeUit = (id: unknown): NodeSingular | null => {
        const element = id ? dlModel.getElement(id as string) : null;
        return (element && element.nonempty()) ? element[0] as NodeSingular : null;
    };

    /**
     * Een node die er moet zijn; ontbreekt hij, dan loopt onze graaf uit de pas met die van de
     * leider en is herladen het enige antwoord.
     */
    const nodeMoetErZijn = (id: unknown): NodeSingular => {
        const node = nodeUit(id);
        if (!node) {
            throw new Error('UNMATCH');
        }
        return node;
    };

    return new Map<string, Actieregel>([

        /* ******** Elementen erbij ******** */

        ['addNode', {
            undo: true,
            vraag: args => ({
                actie: 'addNode',
                position: args['positie'],
                node: (args['node'] as NodeSingular | null)?.id() ?? null,
                type: args['type'],
                data: args['data']
            }),
            lees: stap => {
                const node = stap['node'] ? nodeUit(stap['node']) : null;
                if (stap['node'] && !node) {
                    return null; //een verzoek op een parentnode die wij niet hebben
                }
                return {positie: stap['position'], type: stap['type'], node, data: stap['data']};
            },
            uitvoer: (args, volger) => acties.voegElementToe(args['positie'] as Position, {
                type: args['type'] as string,
                node: args['node'] as NodeSingular | null,
                data: args['data'] as Record<string, unknown> | undefined
            }, volger)
        }],

        ['create', {
            undo: true,
            vanLeider: (stap, context) => {
                acties.neemNodeOver(stap['node'] as NieuweNodeData,
                    !!stap['select_in_origineel'] && context.origineleAfzenderJij);
                return {inhoudGewijzigd: true};
            }
        }],

        ['addRelation', {
            undo: true,
            vraag: args => ({
                actie: 'addRelation',
                van: (args['van'] as NodeSingular).id(),
                naar: (args['naar'] as NodeSingular).id(),
                type: args['type']
            }),
            lees: stap => {
                const van = nodeUit(stap['van']);
                const naar = nodeUit(stap['naar']);
                return (van && naar) ? {van, naar, type: stap['type']} : null;
            },
            uitvoer: (args, volger) => acties.legRelatie(args['van'] as NodeSingular,
                args['naar'] as NodeSingular, args['type'] as string, false, !volger, volger),
            vanLeider: (stap, context) => ({
                ...acties.legRelatie(nodeMoetErZijn(stap['van']), nodeMoetErZijn(stap['naar']),
                    stap['type'] as string, stap['id'] as string,
                    !!stap['select_in_origineel'] && context.origineleAfzenderJij),
                inhoudGewijzigd: true
            })
        }],

        ['addQSE', {
            undo: true,
            vraag: args => ({
                actie: 'addQSE',
                positie: args['positie'],
                qs: (args['qsNode'] as NodeSingular).id()
                //nooit in condset
            }),
            lees: stap => {
                const qsNode = nodeUit(stap['qs']);
                return qsNode ? {qsNode, positie: stap['positie']} : null;
            },
            uitvoer: (args, volger) => acties.voegQsElementToe(args['qsNode'] as NodeSingular,
                args['positie'] as 'top' | 'bottom', volger),
            vanLeider: (stap, context) => {
                acties.neemQsElementOver(nodeMoetErZijn(stap['qs']), stap['positie'] as 'top' | 'bottom',
                    !!stap['select_in_origineel'] && context.origineleAfzenderJij,
                    stap['elementType'] as string | undefined, stap['id'] as string | undefined);
                return {inhoudGewijzigd: true};
            }
        }],

        /* ******** Elementen wijzigen ******** */

        ['switchArgs', {
            undo: true,
            vraag: args => ({actie: 'switchArgs', node: (args['node'] as NodeSingular).id()}),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node} : null;
            },
            uitvoer: (args, volger) => acties.wisselArgumenten(args['node'] as NodeSingular, volger),
            vanLeider: stap => ({
                ...acties.wisselArgumenten(nodeMoetErZijn(stap['node'])),
                inhoudGewijzigd: true
            })
        }],

        ['changeNode', {
            undo: true,
            vraag: args => ({
                actie: 'changeNode',
                node: (args['node'] as NodeSingular).id(),
                type: args['type']
            }),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node, type: stap['type']} : null;
            },
            uitvoer: (args, volger) => acties.wijzigNodeType(args['node'] as NodeSingular,
                args['type'] as string, volger),
            vanLeider: stap => ({
                ...acties.wijzigNodeType(nodeMoetErZijn(stap['node']), stap['type'] as string),
                inhoudGewijzigd: true
            })
        }],

        ['wijzigNaam', {
            undo: true,
            vraag: args => ({
                actie: 'wijzigNaam',
                node: (args['node'] as NodeSingular).id(),
                naam: args['naam']
                //geen invloed op condset
            }),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node, naam: stap['naam']} : null;
            },
            uitvoer: (args, volger) => acties.wijzigNaam(args['node'] as NodeSingular,
                args['naam'] as string, false, volger),
            vanLeider: stap => ({
                ...acties.wijzigNaam(nodeMoetErZijn(stap['node']), stap['naam'] as string, true),
                inhoudGewijzigd: true
            })
        }],

        ['qsZero', {
            undo: true,
            vraag: args => ({
                actie: 'qsZero',
                node: (args['node'] as NodeSingular).id()
                //nooit in condset
            }),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node} : null;
            },
            uitvoer: (args, volger) => acties.toggleQsNulpunt(args['node'] as NodeSingular, volger),
            vanLeider: stap => ({
                ...acties.toggleQsNulpunt(nodeMoetErZijn(stap['node'])),
                inhoudGewijzigd: true
            })
        }],

        /* ******** De vier toggles: de leider stuurt het resultaat als create/delete ******** */

        ['toggleQValue', {
            undo: true,
            vraag: args => ({actie: 'toggleQValue', node: (args['node'] as NodeSingular).id()}),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node} : null;
            },
            uitvoer: (args, volger) => acties.toggleQWaarde(args['node'] as NodeSingular, volger)
        }],

        ['toggleDValue', {
            undo: true,
            vraag: args => ({actie: 'toggleDValue', node: (args['node'] as NodeSingular).id()}),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node} : null;
            },
            uitvoer: (args, volger) => acties.toggleDWaarde(args['node'] as NodeSingular, volger)
        }],

        ['toggleExogeen', {
            undo: true,
            vraag: args => ({
                actie: 'toggleExogeen',
                node: (args['node'] as NodeSingular).id(),
                type: args['type']
                //nooit in condset
            }),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node, type: stap['type']} : null;
            },
            uitvoer: (args, volger) => acties.toggleExogeen(args['node'] as NodeSingular,
                args['type'] as string, volger)
        }],

        ['toggleAllewaarden', {
            undo: true,
            vraag: args => ({
                actie: 'toggleAllewaarden',
                node: (args['node'] as NodeSingular).id()
                //nooit in condset
            }),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node} : null;
            },
            uitvoer: (args, volger) => acties.toggleAlleWaarden(args['node'] as NodeSingular, volger)
        }],

        /* ******** Weghalen ******** */

        ['delete', {
            undo: true,
            vraag: args => ({
                actie: 'delete',
                node: (args['node'] as NodeSingular).id()
                //gaan vanzelf goed in condset
            }),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node ? {node} : null;
            },
            uitvoer: (args, volger) => acties.verwijderNode(args['node'] as NodeSingular, false, volger),
            vanLeider: stap => ({
                ...acties.verwijderNode(nodeMoetErZijn(stap['node']), true),
                inhoudGewijzigd: true
            })
        }],

        /* ******** Stand van het geheel ******** */

        ['positie', {
            //de lokale kant is het einde van een sleep op het canvas (sinds golf 4d in het canvas-eiland):
            //daar staan de nodes al, dus bij leider en solo zet de uitvoer ze idempotent opnieuw en een
            //volger vraagt de leider ze rond te sturen. Geen undo-punt, net als in AngularJS
            vraag: args => ({actie: 'positie', posities: args['posities']}),
            lees: stap => ({posities: stap['posities']}),
            uitvoer: args => acties.zetPosities(args['posities'] as Array<{id: string; pos: Position}>, true),
            //`fit` staat alleen op de posities van een snapNorm: dan is het hele model verschoven en
            //brengt ook de ontvanger zijn beeld op maat
            vanLeider: stap => acties.zetPosities(stap['posities'] as Array<{id: string; pos: Position}>,
                false, stap['fit'] === true)
        }],

        ['snapNorm', {
            //sinds golf 4d een actie van de baas: een volger vraagt het de leider, die met zijn eigen
            //normmatch schuift en de posities rondstuurt (besluit gebruiker, golf 4d). Zonder normmatch
            //valt er niets te schuiven, en ook geen undo-punt te zetten — de lokale aanroeper toetst dat
            //vóór `doe`, de leider hier
            undo: true,
            vraag: () => ({actie: 'snapNorm'}),
            lees: () => normService.normmatch() ? {} : null,
            uitvoer: () => acties.snapNaarNorm(normService.normmatch()?.mappings_op_werk ?? {})
        }],

        ['undo', {
            vraag: () => ({actie: 'undo'}),
            lees: () => opslag.canUndo() ? {} : null,
            uitvoer: (_args, volger) => acties.undoRedo(false, volger)
        }],

        ['redo', {
            vraag: () => ({actie: 'redo'}),
            lees: () => opslag.canRedo() ? {} : null,
            uitvoer: (_args, volger) => acties.undoRedo(true, volger)
        }],

        ['setModeldata', {
            vanLeider: stap => {
                opslag.setData(stap['data'] as Parameters<OpslagService['setData']>[0]);
                return {};
            }
        }],

        ['modelprops', {
            //de eigenaar is de propertiesdialoog; die meldt zijn wijziging aan beide kanten door
            lees: stap => ({props: stap['props']}),
            uitvoer: args => {
                opslag.setProperties(args['props'] as Modelproperties, 'volg');
                return {};
            },
            vanLeider: stap => {
                opslag.setProperties(stap['props'] as Modelproperties, 'leid');
                return {};
            }
        }],

        ['directsave', {
            //een volger die uit de pas liep vraagt om een save, zodat hij kan herladen
            lees: () => ({}),
            uitvoer: () => ({opslaan: 'relevant', direct: true, snapshot: false})
        }],

        ['sync', {
            //een volger die uit een replay terugkomt vraagt om een volledige bijwerking: hij heeft
            //zelf niets gerekend, en bij een herregistratie op hetzelfde model stuurt de leider uit
            //zichzelf niets
            lees: () => ({}),
            uitvoer: () => {
                syncNaarVolgers();
                return {};
            }
        }],

        ['verzoekGeweigerd', {
            //de leider stond zelf in replay en kon het verzoek niet uitvoeren. Het bericht bereikt
            //alle volgers, maar alleen de vrager heeft er iets aan. Een geweigerde `sync` blijft
            //stil: die vrager lost het zelf op, en een toast over een verzoek dat hij niet bewust
            //deed zou alleen verwarren
            vanLeider: (stap, context) => {
                if (context.origineleAfzenderJij && stap['verzoek'] !== 'sync') {
                    toast.toon('SAMENWERKEN.VERZOEK_GEWEIGERD');
                }
                return {};
            }
        }],

        ['norm', {
            //de leider stuurt zijn uitslag mét het antwoord op "wat is hier nieuw": een volger heeft
            //geen eigen montecarlo-uitslag om mee te vergelijken, dus hij neemt dat antwoord over
            vanLeider: stap => {
                normService.verwerkNormdata(stap['interpretaties'] as NormmatchOutput | null,
                    stap['telling'] as Parameters<NormService['verwerkNormdata']>[1],
                    'vanLeider', (stap['nieuw'] ?? []) as string[]);
                return {};
            }
        }],

        /* ******** De ballon ******** */

        ['setBallon', {
            undo: true,
            vraag: args => ({actie: 'setBallon', ballon: args['ballon'], node: args['nodeId']}),
            lees: stap => ({ballon: stap['ballon'], nodeId: stap['node']}),
            uitvoer: (args, volger) => acties.zetBallon(args['ballon'] as Parameters<Modelacties['zetBallon']>[0],
                args['nodeId'] as string, volger),
            vanLeider: stap => acties.zetBallon(stap['ballon'] as Parameters<Modelacties['zetBallon']>[0],
                stap['node'] as string)
        }],

        /* ******** De conditiesets ******** */

        //`showConditionalTools` is met de testronde van a4 vervallen (09-09-2026): het open- en
        //dichtklappen van het conditiesetpaneel is lokaal gedrag geworden en gaat niet meer over de
        //lijn. Wat de sets zélf raakt staat hieronder en reist wel mee.

        ['changeCondtag', {
            undo: true,
            vraag: args => ({
                actie: 'changeCondtag',
                oldname: args['oudeNaam'] ?? null,
                newname: args['nieuweNaam'] ?? null
            }),
            lees: stap => ({oudeNaam: stap['oldname'] ?? null, nieuweNaam: stap['newname'] ?? null}),
            uitvoer: (args, volger) => acties.wijzigConditieset(args['oudeNaam'] as string | null,
                args['nieuweNaam'] as string | null, volger),
            vanLeider: stap => acties.wijzigConditieset(stap['oldname'] as string | null,
                stap['newname'] as string | null)
        }],

        ['setCondtag', {
            undo: true,
            vraag: args => ({actie: 'setCondtag', condtag: args['tag'] ?? null, state: args['state'] ?? null}),
            lees: stap => ({tag: stap['condtag'] ?? null, state: stap['state'] ?? null}),
            uitvoer: (args, volger) => acties.zetConditieset(args['tag'] as string | null,
                args['state'] as 'cond' | 'cons' | null, volger),
            vanLeider: stap => acties.zetConditieset(stap['condtag'] as string | null,
                stap['state'] as 'cond' | 'cons' | null)
        }],

        ['setViewtag', {
            undo: true,
            vraag: args => ({actie: 'setViewtag', condtag: args['tag'], show: args['toon']}),
            lees: stap => ({tag: stap['condtag'], toon: stap['show']}),
            uitvoer: (args, volger) => acties.toonConditieset(args['tag'] as string, !!args['toon'], volger),
            vanLeider: stap => acties.toonConditieset(stap['condtag'] as string, !!stap['show'])
        }],

        ['syncConditiesets', {
            //de conditieset-stand van de leider; alleen een volger neemt hem over. Of het paneel
            //openstaat hoort daar niet bij: dat is lokaal
            vanLeider: stap => acties.neemConditiesetsOver(
                (stap['condtag'] ?? null) as CondtagState | null,
                (stap['conditionele_tags'] ?? {}) as Record<string, boolean>)
        }],

        /* ******** De feedbacklagen ******** */

        ['normToon', {
            //de balk zet zijn eigen vlag; wat hier gebeurt is alleen de route. De staart van een
            //eigen toggle loopt via `meldToggle` in de baas, dus deze uitvoer levert zelf niets op
            lees: stap => ({toon: stap['toon']}),
            uitvoer: (args, volger) => {
                feedbackbalk.voerToggleUit('norm', !!args['toon'], volger ?? undefined);
                return {};
            },
            vanLeider: stap => {
                feedbackbalk.neemToggleOver('norm', !!stap['toon']);
                return {};
            }
        }],

        ['bsToon', {
            lees: stap => bouwsuggestiesoort(stap['soort']) ? {soort: stap['soort'], toon: stap['toon']} : null,
            uitvoer: (args, volger) => {
                feedbackbalk.voerToggleUit(args['soort'] as BouwsuggestieSoort, !!args['toon'], volger ?? undefined);
                return {};
            },
            vanLeider: stap => {
                if (bouwsuggestiesoort(stap['soort'])) {
                    feedbackbalk.neemToggleOver(stap['soort'] as BouwsuggestieSoort, !!stap['toon']);
                }
                return {};
            }
        }],

        ['wisFeedbackselectie', {
            //het wegklikken van een aanwijzing: een klik op het canvas of op een ander element.
            //Zelfde route als het aanwijzen zelf, anders blijft de highlight bij de ander staan
            //(bevinding testronde a4, 09-09-2026). Geen logregel: AngularJS legde een intrekking
            //ook niet vast.
            lees: () => ({}),
            uitvoer: () => {
                feedbackSelectie.wisSelectie();
                return {bericht: [{actie: 'wisFeedbackselectie'}]};
            },
            vanLeider: () => {
                feedbackSelectie.wisSelectie();
                return {};
            }
        }],

        ['highlight_fout', {
            //een volger wijst een normfout aan; de leider neemt hem over, duwt hem door naar de
            //andere volgers en logt op naam van die volger. Loopt onze normmatch een tik achter,
            //dan kennen we de fout niet en gebeurt er niets
            lees: stap => ({elementId: stap['elementId'], foutcode: stap['foutcode']}),
            uitvoer: (args, volger) => {
                const payload = feedbackSelectie.selecteerFout(args['elementId'] as string,
                    args['foutcode'] as string, 'ontvangen');
                return payload
                    ? {
                        bericht: [{actie: 'highlight_fout', elementId: args['elementId'], foutcode: args['foutcode']}],
                        directlog: {actie: 'foutselect', payload, volger}
                        //en een snapshot: sinds besluit 15-09-2026 leggen alle drie de aanwijzingen
                        //hun stand vast, zoals de simfeedback-selectie dat altijd al deed
                    }
                    : {};
            },
            vanLeider: stap => {
                feedbackSelectie.selecteerFout(stap['elementId'] as string, stap['foutcode'] as string, 'ontvangen');
                return {};
            }
        }],

        ['highlight_bouwsuggestie', {
            lees: stap => ({node: stap['node'], regel: stap['regel'], soort: stap['soort']}),
            uitvoer: (args, volger) => {
                const payload = feedbackSelectie.selecteerBouwsuggestie(args['regel'] as string,
                    args['node'] as string, args['soort'] as BouwsuggestieSoort, 'ontvangen');
                return payload
                    ? {
                        bericht: [{
                            actie: 'highlight_bouwsuggestie',
                            node: args['node'],
                            regel: args['regel'],
                            soort: args['soort']
                        }],
                        directlog: {actie: 'sug_select', payload, volger}
                        //en een snapshot; zie `highlight_fout` hierboven
                    }
                    : {};
            },
            vanLeider: stap => {
                feedbackSelectie.selecteerBouwsuggestie(stap['regel'] as string, stap['node'] as string,
                    stap['soort'] as BouwsuggestieSoort, 'ontvangen');
                return {};
            }
        }],

        ['bsAntwoord', {
            //een volger beantwoordt een suggestie. De leider logt altijd — het onderzoek wil ook de
            //weggeklikte suggesties zien — en voert alleen bij status 2 uit: dan verdwijnt de regel
            //uit de set en is het een modelwijziging, waarna de engine-run de nieuwe stand rondstuurt.
            //Bij status 3 verandert de set niet; dat raakt alleen de dialoogvolgorde van die volger.
            vraag: args => ({
                actie: 'bsAntwoord',
                node: (args['node'] as NodeSingular).id(),
                handle: args['handle'],
                antwoord: args['antwoord'],
                status: args['status']
            }),
            lees: stap => {
                const node = nodeUit(stap['node']);
                return node
                    ? {node, handle: stap['handle'], antwoord: stap['antwoord'], status: stap['status']}
                    : null;
            },
            uitvoer: (args, volger) => {
                const node = args['node'] as NodeSingular;
                const handle = args['handle'] as string;
                const logregels = [opslag.legLogVast('sug_antwoord', dlModel.route(node),
                    node.data('type') as string,
                    {antwoord: args['antwoord'], status: args['status'], handle}, volger, false)];
                if (args['status'] !== 2) {
                    return {logregels, snapshot: false}; //puur weergavevolgorde bij de volger
                }
                const regelinfo = bouwsuggestieEngine.regelinfosVoor(node).find(info => info.handle === handle);
                if (!regelinfo) {
                    //de regel matcht bij ons inmiddels niet meer; de eerstvolgende bsSync herstelt hem
                    log.warn('bsAntwoord op onbekende regel geskipt', handle);
                    return {logregels, snapshot: false};
                }
                bouwsuggestieEngine.setRegelstatus(node, regelinfo, args['antwoord'] as 1 | 2 | 3);
                //het undo-punt staat hier ná de mutatie, net als in AngularJS; zie de bevinding
                //over de undo-punten van de feedbacklagen in het testdocument van deze golf
                opslag.setUndoPoint();
                return {logregels, snapshot: false, opslaan: 'relevant'};
            }
        }],

        ['bsSync', {
            //de leider stuurt de uitkomsten van zijn engine-run; volgers runnen zelf nooit.
            //Afgeschermd: een mislukte import is geen model-desync en dus geen herlading waard —
            //de eerstvolgende bsSync herstelt de stand
            vanLeider: stap => {
                try {
                    bouwsuggestieEngine.importeerRun(stap['data'] as Parameters<BouwsuggestieEngineService['importeerRun']>[0]);
                } catch (fout) {
                    log.error('bsSync-import mislukt', fout);
                }
                return {};
            }
        }]
    ]);
}

/**
 * Is dit een van de twee bouwsuggestiesoorten? De `bsToon`-actie draagt hem als vrije waarde.
 */
function bouwsuggestiesoort(soort: unknown): boolean {
    return soort === 'noodzakelijk' || soort === 'gewenst';
}
